import { signIn, signOut, useSession } from 'next-auth/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// import { fetchWorkshopDetailsByProfile } from '../../../services/workshop';
// import { fetchUserProfile } from '../../../services/profile';
import {
  fetchMoreVisitsForDate,
  fetchMoreVisitsOngoing,
  fetchMoreVisitsUnscheduled,
  fetchVisitsForDate,
  fetchVisitsOngoing,
  fetchVisitsUnscheduled,
} from '../../../services/visits';
import {
  appendWorkshopForDateVisits,
  appendWorkshopOngoingVisits,
  appendWorkshopUnscheduledVisits,
  getWorkshopForDateVisits,
  getWorkshopOngoingVisits,
  getWorkshopUnscheduledVisits,
} from '../../../store/actions';
import VisitsScreen from '../../scenes/visits/VisitsScreen';

const VisitsScreenContainer = () => {
  const { data: session, status } = useSession();
  const visitsForDate = useSelector((state) => state.visitsForDateList);
  const visitsOngoing = useSelector((state) => state.visitsOngoingList);
  const visitsUnscheduled = useSelector((state) => state.visitsUnscheduledList);
  const [selectedMenu, setSelectedMenu] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMoreData, setIsLoadingMoreData] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadMoreData, setLoadMoreData] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  // const [profile, setProfile] = useState(null);
  const observer = useRef();
  const dispatch = useDispatch();
  const limit = 10;

  useEffect(() => {
    if (loadMoreData) {
      loadMoreVisits();
    }
    setLoadMoreData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMoreData]);

  const lastVisitRef = useCallback(
    (node) => {
      if (isLoadingMoreData) {
        return;
      }
      if (observer.current) {
        observer.current.disconnect();
      }
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setLoadMoreData(true);
        }
      });
      if (node) observer.current.observe(node);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoadingMoreData, loadMoreData, selectedMenu, hasMore],
  );

  const loadVisits = async () => {
    setIsLoading(true);
    try {
      await refreshVisits();
    } catch (err) {
      setError(err);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (session?.error === 'RefreshAccessTokenError' || status === 'unauthenticated') {
      signIn(); // Force sign in to hopefully resolve error
    }
    if (session?.user && status === 'authenticated') {
      // fetchUserProfileHandler(session?.user);
      loadVisits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.accessToken, session?.error, status]);

  useEffect(() => {
    const refreshContent = async () => {
      if (session?.user) {
        setIsLoading(true);
        switch (selectedMenu) {
          case 0:
            if (!visitsForDate?.visits?.length > 0) {
              await refreshVisitsForDate();
            }
            break;
          case 1:
            if (!visitsOngoing?.visits?.length > 0) {
              await refreshVisitsOngoing();
            }
            break;
          case 2:
            if (!visitsUnscheduled?.visits?.length > 0) {
              await refreshVisitsUnscheduled();
            }
            break;
          default:
            break;
        }
        setIsLoading(false);
      }
    };
    setHasMore(true);
    refreshContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMenu]);

  const onRefresh = async () => {
    setIsRefreshing(true);
    await refreshVisits();
    setIsRefreshing(false);
  };

  // const fetchUserProfileHandler = async () => {
  //   await fetchUserProfile(session?.user?.profileId, setProfile);
  // };

  // const fetchWorkshopHandler = async () => {
  //   await fetchWorkshopDetailsByProfile(
  //     session?.user?.profileId,
  //     setWorkshop,
  //   );
  // };

  const saveVisitsForDate = (data) => {
    if (data !== undefined) {
      dispatch(getWorkshopForDateVisits(session?.user?.profileId, data));
    }
  };

  const saveMoreVisitsForDate = (data) => {
    if (data !== undefined) {
      dispatch(appendWorkshopForDateVisits(session?.user?.profileId, data));
    }
  };

  const saveVisitsOngoing = (data) => {
    if (data !== undefined) {
      dispatch(getWorkshopOngoingVisits(session?.user?.profileId, data));
    }
  };

  const saveMoreVisitsOngoing = (data) => {
    if (data !== undefined) {
      dispatch(appendWorkshopOngoingVisits(session?.user?.profileId, data));
    }
  };

  const saveVisitsUnscheduled = (data) => {
    if (data !== undefined) {
      dispatch(getWorkshopUnscheduledVisits(session?.user?.profileId, data));
    }
  };

  const saveMoreVisitsUnscheduled = (data) => {
    if (data !== undefined) {
      dispatch(appendWorkshopUnscheduledVisits(session?.user?.profileId, data));
    }
  };

  const refreshVisitsUnscheduled = async () => {
    await fetchVisitsUnscheduled(
      session.user?.profileId,
      setError,
      saveVisitsUnscheduled,
      logoutHandler,
    );
  };

  const loadMoreVisitsUnscheduled = async () => {
    setHasMore(visitsUnscheduled?.next);
    if (visitsUnscheduled?.next) {
      if (!isLoadingMoreData) {
        setIsLoadingMoreData(true);
        await fetchMoreVisitsUnscheduled(
          visitsUnscheduled?.next,
          setError,
          saveMoreVisitsUnscheduled,
          logoutHandler,
        );
        setIsLoadingMoreData(false);
      }
    }
  };

  const refreshVisitsOngoing = async () => {
    await fetchVisitsOngoing(session.user?.profileId, setError, saveVisitsOngoing, logoutHandler);
  };

  const loadMoreVisitsOngoing = async () => {
    setHasMore(visitsOngoing?.next);
    if (visitsOngoing?.next) {
      if (!isLoadingMoreData) {
        setIsLoadingMoreData(true);
        await fetchMoreVisitsOngoing(
          visitsOngoing?.next,
          setError,
          saveMoreVisitsOngoing,
          logoutHandler,
        );
        setIsLoadingMoreData(false);
      }
    }
  };

  const refreshVisitsForDate = async () => {
    await fetchVisitsForDate(
      session.user?.profileId,
      null,
      setError,
      saveVisitsForDate,
      logoutHandler,
    );
  };

  const loadMoreVisitsForDate = async () => {
    setHasMore(visitsForDate?.next);
    if (visitsForDate?.next) {
      if (!isLoadingMoreData) {
        setIsLoadingMoreData(true);
        await fetchMoreVisitsForDate(
          visitsForDate?.next,
          null,
          setError,
          saveMoreVisitsForDate,
          logoutHandler,
        );
        setIsLoadingMoreData(false);
      }
    }
  };

  const refreshVisits = async () => {
    switch (selectedMenu) {
      case 0:
        await refreshVisitsForDate();
        break;
      case 1:
        await refreshVisitsOngoing();
        break;
      case 2:
        await refreshVisitsUnscheduled();
        break;
      default:
        break;
    }
  };

  const loadMoreVisits = async () => {
    switch (selectedMenu) {
      case 0:
        await loadMoreVisitsForDate();
        break;
      case 1:
        await loadMoreVisitsOngoing();
        break;
      case 2:
        await loadMoreVisitsUnscheduled();
        break;
      default:
        break;
    }
  };

  const logoutHandler = async () => {
    await signOut();
  };

  return (
    <VisitsScreen
      status={status}
      visitsForDate={visitsForDate}
      visitsOngoing={visitsOngoing}
      visitsUnscheduled={visitsUnscheduled}
      loadMoreVisitsForDate={loadMoreVisitsForDate}
      loadMoreVisitsOngoing={loadMoreVisitsOngoing}
      loadMoreVisitsUnscheduled={loadMoreVisitsUnscheduled}
      isLoadingMoreData={isLoadingMoreData}
      limit={limit}
      setSelectedMenu={setSelectedMenu}
      selectedMenu={selectedMenu}
      isLoading={isLoading}
      isRefreshing={isRefreshing}
      onRefresh={onRefresh}
      lastVisitRef={lastVisitRef}
      error={error}
    />
  );
};

export default VisitsScreenContainer;
